import { Tooltip } from "@component/tooltip";
import { ArrowDownIcon, ArrowUpIcon, LucideIcon } from "lucide-react";

interface Props {
    label: string;
    value: string | number;
    icon: LucideIcon;
    suffix?: string;
    trend?: "up" | "down" | "neutral" | string;
    trendValue?: string;
    isOffTrack?: boolean;
    onClick?: () => void;
    tooltip?: string;
    isLoading?: boolean;
}

export default function ExecutiveSummaryCard({
    label,
    value,
    icon: Icon,
    suffix = "",
    trend = "neutral",
    trendValue,
    tooltip,
}: Props) {
    const cardContent = (
        <div
            className="bg-card border border-border rounded-lg p-4 hover:border-blue-500 hover:shadow-lg transition-all duration-200 relative min-w-[280px] lg:min-h-[118px]"
            style={{
                paddingTop: 20,
                paddingRight: 20,
                paddingBottom: 20,
                paddingLeft: 20,
            }}
        >
            <div className="flex items-center gap-4">
                {/* Icon */}
                <div className="flex-shrink-0 w-10 h-10">
                    <div className="w-full h-full flex items-center justify-center bg-blue-50 dark:bg-blue-950 rounded-lg">
                        <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                </div>
                {/* Header and Number */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                            {label}
                        </p>
                        {/* Trend Indicator - Aligned with Header */}
                        {trend && trendValue && (
                            <div
                                className={`flex items-center gap-1 text-sm font-medium ${trend === "up"
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-red-600 dark:text-red-400"
                                    }`}
                            >
                                {trend === "up" ? (
                                    <ArrowUpIcon className="h-3 w-3" />
                                ) : (
                                    <ArrowDownIcon className="h-3 w-3" />
                                )}
                                <span>{trendValue}</span>
                            </div>
                        )}
                    </div>
                    <div className="flex items-baseline gap-1">
                        <p className="text-2xl font-bold text-foreground">
                            {value}
                            {suffix && (
                                <span className="text-sm font-normal text-muted-foreground ml-1">
                                    {suffix}
                                </span>
                            )}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    if (tooltip) {
        return (
            <Tooltip content={(<p>{tooltip}</p>)}>
                {cardContent}
            </Tooltip>
        );
    }

    return cardContent;
}
